import { useState } from 'react';

const faqs = [
  {
    q: 'UTMの導入費用はどのくらいかかりますか？',
    a: '企業の規模や選ぶ機器によって異なりますが、中小企業向けの場合は初期費用と月額費用を合わせてリーズナブルにご提案できます。まずは無料相談でお気軽にご確認ください。具体的なお見積りは現状をヒアリングしたうえでご提示します。',
  },
  {
    q: '従業員数が少ない小規模な会社でも導入できますか？',
    a: 'はい、まったく問題ありません。社員数名の会社様にも多数ご導入いただいています。規模に合わせた最適なプランをご提案しますので、「うちには必要ない」と判断する前にぜひご相談ください。',
  },
  {
    q: '導入後のサポート体制はどうなっていますか？',
    a: '機器の設置・設定から、導入後の運用サポートまで継続してお付き合いします。トラブルが起きた際はお電話やリモートでご対応。定期的な状況確認やアップデート対応も行っています。地域密着だからこそ、迅速な現地対応も可能です。',
  },
  {
    q: '社内にIT担当者がいなくても使いこなせますか？',
    a: 'UTMは一度設定すれば、基本的には自動で機能します。日常的な操作は不要ですので、IT知識がなくても問題ありません。設定変更が必要な場合もこちらで対応しますので、安心してお任せください。',
  },
  {
    q: '今使っているネットワーク環境を変えずに導入できますか？',
    a: 'はい、既存の環境に合わせた形での導入が可能です。現在のルーターやインターネット回線をそのまま活かしながら、UTMを組み込むことができます。導入前のヒアリングで現状を確認してから最適な方法をご提案します。',
  },
  {
    q: '不要になったパソコンの処分も対応してもらえますか？',
    a: 'はい、不要なPCの引き取りにも対応しています。データ消去も対応可能ですので、廃棄時の情報漏洩リスクもあわせて解消できます。詳しくはお問い合わせください。',
  },
  {
    q: 'PC引き取りに費用はかかりますか？',
    a: '完全無料です。出張費・引き取り費・データ消去費のいずれも0円でご対応しています。「費用がかかるかもしれない」とご心配の必要は一切ありません。大和市・座間市・綾瀬市・相模原市・町田市エリアへの出張対応も無料ですので、お気軽にご連絡ください。',
    tag: 'PC引き取り',
  },
  {
    q: 'データは本当に消えますか？消去後の証明はありますか？',
    a: 'はい、専用のデータ消去ソフトを使用して確実に消去します。単なる「削除」「初期化」とは異なり、復元不可能な状態にデータを上書き消去します。消去完了後は「データ消去証明書」を発行しますので、コンプライアンス対応や社内管理の記録としてもご活用いただけます。',
    tag: 'PC引き取り',
  },
  {
    q: '何台でも引き取ってもらえますか？古い機種でも大丈夫ですか？',
    a: '1台から大量の台数まで対応しています。古いPCや年式・メーカーを問わず引き取り可能です。ただし、機器の状態や種別によっては対応できない場合もございます。まずはお電話またはメールにてご相談いただければ、対応可否を素早くご確認します。',
    tag: 'PC引き取り',
  },
];

const FAQSection: React.FC = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<'all' | 'security' | 'pc'>('all');

  const filteredFaqs = faqs.filter((faq) => {
    if (activeTab === 'all') return true;
    if (activeTab === 'pc') return faq.tag === 'PC引き取り';
    return !faq.tag;
  });

  return (
    <section id="faq" className="py-24 bg-slate-50">
      <div className="max-w-3xl mx-auto px-6">
        <div className="text-center mb-12">
          <span className="inline-block text-sky-600 text-sm font-bold tracking-widest uppercase mb-4">
            よくある質問
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-slate-800 leading-tight">
            FAQ
          </h2>
          <p className="mt-4 text-slate-500 text-base max-w-xl mx-auto leading-relaxed">
            導入を検討されている方からよくいただくご質問をまとめました。
          </p>
        </div>

        {/* Tab switcher */}
        <div className="flex justify-center mb-8">
          <div className="inline-flex bg-white rounded-full p-1 border border-slate-200">
            {[
              { key: 'all', label: 'すべて' },
              { key: 'security', label: 'セキュリティ・UTM' },
              { key: 'pc', label: 'PC引き取り' },
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => { setActiveTab(tab.key as 'all' | 'security' | 'pc'); setOpenIndex(null); }}
                className={`whitespace-nowrap px-5 py-2 rounded-full text-xs font-semibold transition-all duration-200 cursor-pointer ${
                  activeTab === tab.key
                    ? 'bg-sky-600 text-white'
                    : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-col gap-3">
          {filteredFaqs.map((faq, i) => (
            <div
              key={i}
              className={`bg-white rounded-xl border overflow-hidden ${
                faq.tag === 'PC引き取り' ? 'border-emerald-100' : 'border-slate-100'
              }`}
            >
              <button
                onClick={() => setOpenIndex(openIndex === i ? null : i)}
                className="w-full text-left px-6 py-5 flex items-start gap-4 cursor-pointer hover:bg-slate-50 transition-colors duration-150"
              >
                {faq.tag === 'PC引き取り' && (
                  <span className="bg-emerald-100 text-emerald-700 text-xs font-bold px-2 py-0.5 rounded-full flex-shrink-0 mt-0.5 hidden sm:block">
                    PC引き取り
                  </span>
                )}
                <span className="text-sky-600 font-bold text-sm mt-0.5 flex-shrink-0">Q.</span>
                <span className="font-semibold text-slate-700 text-sm flex-1 text-left">{faq.q}</span>
                <span className="text-slate-400 text-sm font-bold flex-shrink-0 mt-0.5">
                  {openIndex === i ? '−' : '+'}
                </span>
              </button>
              {openIndex === i && (
                <div className="px-6 pb-5 flex gap-4">
                  <span className="text-teal-600 font-bold text-sm mt-0.5 flex-shrink-0">A.</span>
                  <p className="text-slate-500 text-sm leading-relaxed">{faq.a}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
